// Feature flags, and the Labs switch that forces every one of them on.
//
// Everything after the Classic pad arrived one slice at a time, and a slice
// that was half built could not be reachable by accident — a tab that opens a
// screen with nothing in it is worse than no tab. Each unfinished area got a
// flag, shipped OFF, and the lead flipped it the day its slice landed. All
// of them have now landed, so all of them default ON — a future half-built
// slice still gets its own flag, starting OFF, the same way these did.
//
// Labs is the developer's way in: a hidden switch at the foot of Settings that
// turns every flag on at once, so an unfinished feature can be used and
// photographed without a rebuild. It is stored, so it survives a reload. With
// every flag already on by default, Labs still matters the day a new flag
// starts OFF — and a per-flag value stored in `prefs` still wins over the
// default when Labs itself is off.
//
// ROUTES ARE NOT FLAGGED. A flag hides a DESTINATION — the tab, the Settings
// row — and never the route itself, because a deep link that 404s is a bug
// report and a hidden tab is a decision.

import { reactive, ref } from 'vue';
import { prefs } from '../core/storage.js';

/** Where the per-flag map and the Labs switch live in `prefs`. */
export const FLAGS_KEY = 'pref.flags';
export const LABS_KEY = 'pref.labs';

/** Every flag, and what it ships as. Every slice has landed, so the ones that
 *  hid a destination are true.
 *
 *  RACING is a tab that TOOK one rather than added one — the pad holds five
 *  destinations and racing is the third of them, so Strategies moved under
 *  Settings to make room (its route is untouched and its flag still gates the
 *  Settings row). Switching RACING off hides the tab and leaves `/racing` and
 *  `/race/:matchKey` reachable, which is what a flag has always meant here.
 *
 *  SHARP_ANCHOR is the odd one and is OFF: it hides a strategy rather than a
 *  destination. Sharp Anchor is anchored on Betfair or Pinnacle and neither is
 *  on the AU board the app buys, so it printed nothing every day; the document
 *  and the model are both still there (`strategies/catalogue.js` RETIRED).
 *
 *  IT COMES ALIVE WITH A BETFAIR KEY. Connect the Betfair Exchange in Settings
 *  (an app key plus a login — lib/betfair.js) and the exchange's own price
 *  lands on every match the board carries, which is exactly the sharp opinion
 *  this strategy is anchored on. The default stays OFF: a punter without that
 *  key would get the same blank pad it always printed, so the flag is how
 *  somebody who HAS one puts it back on the strategies list. */
export const FLAG_DEFAULTS = Object.freeze({
  STRATEGIES: true,
  BACKTEST: true,
  PROVIDERS: true,
  CLV: true,
  INSIGHTS: true,
  RACING: true,
  SHARP_ANCHOR: false,
  // "What do you want?" in place of the dealt pad (docs/tips-ux.md). On, and
  // the old pad is one switch away until the new one is verified on a device.
  ASK: true,
});

export const FLAG_NAMES = Object.keys(FLAG_DEFAULTS);

/** The live flags. Read them in a template or a computed; never write them. */
export const flags = reactive({ ...FLAG_DEFAULTS });

/** True when the hidden Labs switch is on. */
export const labs = ref(false);

/**
 * What the flags are, given what is stored. Pure, so the rule is testable
 * without touching localStorage: Labs on means every flag on, whatever the
 * stored map says; Labs off means the stored map, defaulted.
 */
export function resolveFlags(stored, labsOn) {
  const out = { ...FLAG_DEFAULTS };
  if (labsOn === true) {
    for (const name of FLAG_NAMES) out[name] = true;
    return out;
  }
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    for (const name of FLAG_NAMES) {
      if (typeof stored[name] === 'boolean') out[name] = stored[name];
    }
  }
  return out;
}

/** Re-read the stored flags into the live object. Returns the live object. */
export function loadFlags() {
  const on = prefs[LABS_KEY] === true;
  labs.value = on;
  Object.assign(flags, resolveFlags(prefs[FLAGS_KEY], on));
  return flags;
}

/** Turn Labs on or off, store it, and recompute. */
export function setLabs(on) {
  prefs[LABS_KEY] = on === true;
  return loadFlags();
}

// At import, not at mount: the tab bar asks what the flags are while the shell
// is rendering, and localStorage answers synchronously.
loadFlags();
